/**
 * Canonical tier pricing — single source of truth.
 *
 * Per K12 monetization model (CLAUDE.md):
 *   - coach: $9/mo or $79/yr
 *   - org:   $59/mo or $499/yr
 *
 * Used by mobile + PWA + marketing pages.
 *
 * NEVER hardcode dollar amounts elsewhere — import from here.
 * Stripe-side prices are environment variables (`STRIPE_PRICE_*`) and
 * are the source of truth for actual billing; this file is the source
 * of truth for display copy only.
 */

export const PRICING = {
  coach: {
    monthly: { display: '$9',   suffix: '/mo',   priceUSD: 900   },
    annual:  { display: '$79',  suffix: '/yr',   priceUSD: 7900  },
  },
  org: {
    monthly: { display: '$59',  suffix: '/mo',   priceUSD: 5900  },
    annual:  { display: '$499', suffix: '/yr',   priceUSD: 49900 },
  },
} as const

export type TierKey      = keyof typeof PRICING
export type BillingPeriod = keyof (typeof PRICING)['coach']

/**
 * Annual savings vs 12× monthly. Used for "save $X" copy.
 */
export const ANNUAL_SAVINGS = {
  coach: (PRICING.coach.monthly.priceUSD * 12 - PRICING.coach.annual.priceUSD) / 100, // 29
  org:   (PRICING.org.monthly.priceUSD   * 12 - PRICING.org.annual.priceUSD)   / 100, // 209
} as const

/**
 * Music Plus — $4.99/mo Coach add-on (GREEN-LIT 2026-05-14).
 *
 * Unlocks: unlimited AI walk-up song generations, full ASCAP/BMI catalog,
 * Music Live Activity push, Season Highlights Reel auto-stitch, premium
 * picker UX with preview.
 *
 * Free tier caps:
 *   - 3 AI song generations / season (per team)
 *   - 100 royalty-free catalog tracks
 *
 * Bundled FREE with annual Coach ($79/yr) and Org tier per anchor refinement.
 *
 * Stripe price env var: STRIPE_PRICE_MUSIC_PLUS_MONTHLY (set after creating
 * the product in the Stripe dashboard — see migration 20260514190000).
 *
 * Y1 ARR target: $30K–$60K → $200K+ at 10K coaches.
 */
export const MUSIC_PLUS = {
  monthly: { display: '$4.99', suffix: '/mo', priceUSD: 499 },
  /** Free-tier per-season AI song generation cap before paywall kicks in. */
  freeTierAiGenerations: 3,
  /** Free-tier royalty-free catalog size. Music Plus opens the full catalog. */
  freeTierCatalogSize: 100,
} as const

/**
 * Tournament Hosting tier — $199 base + $9/team registered.
 *
 * Revenue verdict from `chalk-os/audits/synthesis/2026-05-14-product-value-
 * and-monetization-verdict.md` §"Top 3 revenue moves": existing tournament
 * organizers pay nothing today; this tier captures $50K–$150K Y1 ARR.
 *
 * Constitution constraint: parents NEVER charged. The $199 is paid by the
 * tournament HOST (a coach or director publishing the tournament). The $9
 * per-team fee is paid by the registering COACH, not by parents. There is
 * no parent-facing tournament payment surface — `PaymentGate` is role-
 * gated and refuses to render for parent accounts.
 *
 * Stripe price env vars (operator creates the products in the Stripe
 * dashboard, copies the price IDs into Vercel):
 *
 *   STRIPE_PRICE_TOURNAMENT_HOSTING   — one-time price $199
 *   STRIPE_PRICE_TOURNAMENT_PER_TEAM  — one-time price $9
 *
 * Comp path: tournaments.is_comp = true skips the gate entirely (used for
 * white-glove acquisition / loss-leader onboarding for high-volume hosts).
 *
 * NEVER hardcode "$199" or "$9 per team" elsewhere — always resolve from
 * here. The lock test
 * `apps/mobile/v2/__tests__/tournament-pricing-lock.test.ts` enforces it.
 */
export const PRICING_TOURNAMENT = {
  hostingFee:    { display: '$199', suffix: ' flat', priceUSD: 19900 },
  perTeam:       { display: '$9',   suffix: ' / team', priceUSD: 900 },
} as const

export type TournamentPriceKey = keyof typeof PRICING_TOURNAMENT

/**
 * ─── FEES — Canonical fee disclosure constants ───────────────────────────
 *
 * Single source of truth for EVERY user-visible fee disclosure. Any string
 * that quotes a percentage to a payer, payee, coach, parent, trainer, or
 * admin MUST resolve to these values — directly via `FEES.*Display` or
 * computed via `FEES.*Percent`.
 *
 * Why this exists
 * ---------------
 * Pre-2026-05-14 audit (`chalk-os/audits/monetization/2026-05-14-
 * revenue-expansion-strategy.md`) found three lying disclosures across the
 * web surface:
 *
 *   1. Email receipts hardcoded "2.5%" while runtime `CHALK_PERCENTAGE`
 *      in `apps/web/lib/fees.ts` was 5% — payers were quoted half the
 *      actual deduction.
 *   2. `/for-trainers` and trainer signup said "1% per session" while the
 *      runtime `PLATFORM_FEE_RATE` in `apps/web/app/api/trainer/[handle]/
 *      book/route.ts` and `packages/*` routes was 10% — a 10× understate.
 *   3. Misc team-training modals hardcoded "2.5%" while team training
 *      payments go through `calcApplicationFee` at 1%.
 *
 * Three numbers. Three lies. One legal exposure surface under state
 * consumer-protection statutes.
 *
 * The fix is structural: bind every disclosure to a constant that MUST
 * track the runtime rate. The lock test
 * `apps/mobile/v2/__tests__/fees-disclosure-lock.test.ts` asserts the
 * runtime rate files (`apps/web/lib/fees.ts`, `apps/web/lib/stripe/
 * helpers.ts`, the trainer book/packages routes) still match these
 * constants, so future drift fails CI loud.
 *
 * NEVER hardcode a fee percentage in a user-facing string. Always import
 * from here and use `FEES.*Display`.
 */
export const FEES = {
  /** Team payment requests (coach-charged events, etc.) — 5% via fees.ts CHALK_PERCENTAGE. */
  teamPlatformPercent: 0.05,
  teamPlatformDisplay: '5%',

  /** Team training session checkout — 1% via lib/stripe/helpers.ts calcApplicationFee. */
  teamTrainingPercent: 0.01,
  teamTrainingDisplay: '1%',

  /**
   * Trainer marketplace single sessions & packages — 3%.
   *
   * Sprint 61.40 (2026-05-14): dropped from 10% → 3% per pricing-strategist
   * verdict. Sits in the competitive trainer-marketplace floor (Vagaro 3%,
   * Square Appointments 2.6%, PocketSuite 2.9%). Honors the K12 constitution
   * that trainers are a near-free acquisition surface for Chalk.
   *
   * THESE CONSTANTS ARE NOW THE FAIL-CLOSED FALLBACK. The runtime source of
   * truth is the `platform_fees` Supabase table, read via
   * `getPlatformFee('trainer_session')`. Constants here are reached only if
   * the DB read fails — keeping rates consistent with the most-recent
   * intentional rate, never 0%.
   */
  trainerPlatformPercent: 0.03,
  trainerPlatformDisplay: '3%',

  /** Stripe processing — passed through to payer when fees are absorbed by coach/seller. */
  stripeProcessingPercent: 0.029,
  stripeProcessingPercentDisplay: '2.9%',
  stripeProcessingFixed: 0.30,
  stripeProcessingFixedDisplay: '$0.30',
} as const

export type FeesKey = keyof typeof FEES
